import { z } from 'zod';
import { AuditSchema, EntitySchema, ProcessedImageSchema } from './entities/shared.schema';
import { WriterRoomStatusSchema } from './room-status.schema';

/**
 * Scene schema.
 * Master source for Scene entity structure.
 */
export const SceneSchema = EntitySchema.extend({
  primaryImageId: z.string().nullable().optional(),
  audit: AuditSchema.optional(),
  images: z.array(ProcessedImageSchema).optional().default([]),
  name: z.string().min(2, "Scene name must be at least 2 characters."),
  chapterId: z.string().describe('ID of the chapter this scene belongs to.').optional(),
  chapterOrder: z.number().int().describe('Which chapter this scene is in (for quick reference).').optional(),
  order: z.number().int().describe('Position of this scene within its chapter.').optional(),
  eventIds: z.array(z.string()).describe('References to Events occurring in the scene.').optional(),
  characterIds: z.array(z.string()).describe('References to Characters present in the scene.').optional(),
  povCharacterId: z.string().describe('ID of the character whose point of view is used in this scene.').optional(),
  minorCharacters: z.string().describe('A description of minor or background characters in the scene.').optional(),
  organizationIds: z.array(z.string()).describe('References to Organizations involved in the scene.').optional(),
  locationIds: z.array(z.string()).describe('References to Locations where the scene takes place.').optional(),
  settings: z.string().describe('A description of the specific setting within the locations.').optional(),
  pointOfScene: z.string().describe('The primary purpose or goal of the scene.').optional(),
  themes: z.string().describe('Key themes explored in the scene.').optional(),
  goal: z.string().describe('The primary goal of the scene.').optional(),
  conflict: z.string().describe('The primary conflict or obstacle in the scene.').optional(),
  changesToMake: z.string().describe('Notes on future edits or changes for this scene.').optional(),
  status: WriterRoomStatusSchema.default({ phase: 'idle' }).describe('Current status of the scene.'),
  proposal: z.object({
    verdict: z.enum(['pass', 'needs_more_detail', 'suggest_split']),
    analysis: z.string(),
    proposedScenes: z.array(z.object({
      name: z.string(),
      description: z.string(),
      goal: z.string(),
      eventIds: z.array(z.string()),
    })),
    createdAt: z.string(),
  }).optional(),
  mainContentId: z.string().describe('ID of the Content object (text) associated with this scene.').optional(),
  isSnapshotCreated: z.boolean().optional().describe('Whether a state snapshot has been created for this scene.'),
  constraints: z.array(z.string()).optional().describe('Style or plot constraints for AI generation.'),
  contentVariants: z.array(z.string()).optional().describe('List of variants for this scene.'),
  contentVariantVersions: z.record(z.string(), z.number().int().nonnegative()).optional().describe('Latest scratchpad version for every scene content variant.'),
});

export type Scene = z.infer<typeof SceneSchema>;
